import React, { useMemo, useState } from "react";
import { Text, Button, DataCard, type LineDiagramTypes } from "@origo/sp-lib";
import styled from "styled-components";
import { useSingleLine } from "../context/context";
import TurbineSettingsConfirmation from "./TurbineSettingsConfirmation";
import { TurbineOptions } from "../context/types";

const Container = styled.div`
  display: flex;
  gap: 1rem;
  height: 100%;
`;

const Indicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0.5rem 0;
`;

const Label = styled.div`
  display: flex;
  gap: 1rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 20vw;
`;

const VerticalSeparator = styled.div`
  border: 1px solid grey;
`;

const VerticalDivisor = styled.hr`
  margin-bottom: 1rem;
`;

type TurbineSettingsProps = {
  node: LineDiagramTypes.Node;
};

export default function TurbineSettings({ node }: TurbineSettingsProps) {
  const {
    getNode,
    toggleTurbineStatus,
    toggleTurbineInterlock,
    toggleTurbinePersonnel,
  } = useSingleLine();

  const turbine = useMemo(() => {
    const turbine = getNode(node.data.id as string);

    return {
      status:
        turbine?.status?.isTurnedOff === undefined ||
        !turbine?.status?.isTurnedOff
          ? "Connected"
          : "Disconnected",
      action:
        turbine?.status?.isTurnedOff === undefined ||
        !turbine?.status?.isTurnedOff
          ? "Disconnect"
          : "Connect",
      interlock: turbine?.interlock,
      personnel: turbine?.personnel,
    };
  }, [getNode]);

  const [statusDialogueOpen, setStatusDialogueOpen] = useState(false);
  const [personnelDialogueOpen, setPersonnelDialogueOpen] = useState(false);
  const [interLockDialogueOpen, setInterLockDialogueOpen] = useState(false);

  return (
    <Container>
      <Section>
        <Text size="lg">Controls</Text>
        <VerticalDivisor />
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Indicator>
            <Label>
              <Text>Status:</Text>
              <Text>{turbine.status}</Text>
            </Label>
            <Button onClick={() => setStatusDialogueOpen(true)}>
              {turbine.action}
            </Button>
          </Indicator>
          <TurbineSettingsConfirmation
            turbineOption={TurbineOptions.turbineStatus}
            turbineValue={turbine.status ? "connect" : "disconnect"}
            open={statusDialogueOpen}
            onClick={(isConfirmed) => {
              if (isConfirmed) {
                toggleTurbineStatus(node.data.id as string);
                setStatusDialogueOpen(false);
              } else {
                setStatusDialogueOpen(false);
              }
            }}
          />
          <Indicator>
            <Label>
              <Text>Personnel on turbine:</Text>
              <Text>{turbine.personnel ? "Yes" : "No"}</Text>
            </Label>
            <Button onClick={() => setPersonnelDialogueOpen(true)}>
              {turbine.personnel ? "Remove" : "Add"}
            </Button>
          </Indicator>
          <TurbineSettingsConfirmation
            turbineOption={TurbineOptions.personnelStatus}
            turbineValue={turbine.personnel ? "remove" : "add"}
            open={personnelDialogueOpen}
            onClick={(isConfirmed) => {
              if (isConfirmed) {
                toggleTurbinePersonnel(node.data.id as string);
                setPersonnelDialogueOpen(false);
              } else {
                setPersonnelDialogueOpen(false);
              }
            }}
          />
          <Indicator>
            <Label>
              <Text>Interlock:</Text>
              <Text>{turbine.interlock ? "On" : "Off (Unlocked)"}</Text>
            </Label>

            <Button onClick={() => setInterLockDialogueOpen(true)}>
              {turbine.interlock ? "Delete interlock" : "Add interlock"}
            </Button>
          </Indicator>
          <TurbineSettingsConfirmation
            turbineOption={TurbineOptions.interlockStatus}
            turbineValue={turbine.interlock ? "unlock" : "lock"}
            open={interLockDialogueOpen}
            onClick={(isConfirmed) => {
              if (isConfirmed) {
                toggleTurbineInterlock(node.data.id as string);
                setInterLockDialogueOpen(false);
              } else {
                setInterLockDialogueOpen(false);
              }
            }}
          />
        </div>
      </Section>
      <VerticalSeparator />
      <Section>
        <Text size="lg">Status</Text>
        <VerticalDivisor />
        <DataCard
          items={[
            { name: "Active Power", value: 10, unit: ".Mk" },
            { name: "Reactive Power", value: 10, unit: ".Mk" },
            { name: "Average Current", value: 10, unit: ".Mk" },
            { name: "Average Voltage", value: 10, unit: ".Mk" },
            { name: "High-res Grounded", value: 10, unit: ".Mk" },
            { name: "Low-res Grounded", value: 10, unit: ".Mk" },
          ]}
          itemsPerLine={3}
          variant="secondary"
        />
      </Section>
    </Container>
  );
}
